/**
 * 图片路径处理工具
 * 处理开发环境和生产环境的图片路径差异
 */

/**
 * 检查当前环境是否是开发环境
 * 兼容客户端和服务端
 * @returns {boolean} 是否为开发环境
 */
function isDevelopment() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    return __ENV__ === 'development';
  }
  // Node环境
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  return false;
}

/**
 * 检查当前环境是否是生产环境
 * 兼容客户端和服务端
 * @returns {boolean} 是否为生产环境
 */
function isProduction() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    return __ENV__ === 'production' || __ENV__ === 'release';
  }
  // Node环境
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'release';
  }
  return false;
}

/**
 * 获取站点配置
 * 优先从 window.__INITIAL_SITE_CONFIG__ 获取
 * @returns {Object|null} 站点配置对象
 */
function getSiteConfig() {
  if (typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__) {
    return window.__INITIAL_SITE_CONFIG__;
  }
  return null;
}

/**
 * 获取图片前缀
 * 开发环境："/images"
 * 生产环境："/website/images"
 * @returns {string} 图片路径前缀
 */
export function getImagePrefix() {
  return isDevelopment() ? '/images' : '/website/images';
}

/**
 * 标准化图片路径
 * 将路径统一转换为当前环境的标准格式
 * @param {string} path - 图片路径
 * @returns {string} 标准化后的路径
 */
function normalizeImagePath(path) {
  if (!path || typeof path !== 'string') return path;
  
  if (isDevelopment()) {
    // 开发环境：统一为 /images/ 开头
    return path.replace(/^\/website\/images\//, '/images/');
  } else {
    // 生产环境：统一为 /website/images/ 开头
    return path.replace(/^\/images\//, '/website/images/');
  }
}

/**
 * 为路径添加域名前缀
 * @param {string} path - 图片路径
 * @param {string} prefix - 域名前缀（如 cdnPrefix 或 ApiDomainName）
 * @returns {string} 带前缀的完整路径
 */
function addDomainPrefix(path, prefix) {
  if (!path || !prefix) return path;
  
  // 如果路径以 /website/ 开头，直接添加前缀
  if (path.startsWith('/website/')) {
    return `${prefix}${path}`;
  }
  // 如果路径以 /images/ 开头，转换为 /website/images/ 再添加前缀
  if (path.startsWith('/images/')) {
    return `${prefix}/website${path}`;
  }
  // 其他情况，如果路径以 / 开头，直接添加前缀
  if (path.startsWith('/')) {
    return `${prefix}${path}`;
  }
  
  return path;
}

/**
 * 处理图片路径
 * - 如果 useCdn 为 true，使用 cdnPrefix
 * - 如果 useCdn 为 false，使用 ApiDomainName
 * @param {string} path - 图片路径
 * @returns {string} 处理后的路径
 */
export function processCdnPath(path) {
  // 非生产环境或无法获取配置，返回原路径
  if (!isProduction()) {
    return path;
  }
  
  const config = getSiteConfig();
  if (!config) {
    return path;
  }
  
  // 如果 useCdn 为 true，使用 cdnPrefix
  if (config.useCdn && config.cdnPrefix) {
    return addDomainPrefix(path, config.cdnPrefix);
  }
  
  // 如果 useCdn 为 false，使用 ApiDomainName
  if (config.ApiDomainName) {
    return addDomainPrefix(path, config.ApiDomainName);
  }
  
  return path;
}

/**
 * 获取当前站点语言
 * 兼容客户端和服务端
 * @returns {string} 站点语言代码
 */
function getCurrentSiteLanguage() {
  // 浏览器环境
  if (typeof window !== 'undefined') {
    // 尝试从全局变量获取
    if (typeof __SITE_PROFILE__ !== 'undefined') {
      return __SITE_PROFILE__;
    }
    // 尝试从 window.__INITIAL_SITE_CONFIG__ 获取
    const config = getSiteConfig();
    if (config?.profile) {
      return config.profile;
    }
  }
  
  // 服务端环境，尝试从环境变量获取
  if (typeof process !== 'undefined' && process.env?.SITE_PROFILE) {
    return process.env.SITE_PROFILE;
  }
  
  // 默认返回 fr
  return 'fr';
}

/**
 * 获取完整图片路径
 * @param {string} path - 图片相对路径（相对于语言目录）
 * @param {string} lang - 语言代码，默认为当前站点语言
 * @returns {string} 完整图片路径
 */
export function getImagePath(path, lang = null) {
  if (!path || typeof path !== 'string') {
    return path;
  }
  
  try {
    // 如果已经是完整的 URL，直接返回
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // 如果路径已经是完整路径（以/开头），标准化后处理
    if (path.startsWith('/')) {
      const normalizedPath = normalizeImagePath(path);
      return processCdnPath(normalizedPath);
    }
    
    // 如果是相对路径，添加语言和前缀
    const currentLang = lang || getCurrentSiteLanguage();
    const prefix = getImagePrefix();
    const fullPath = `${prefix}/${currentLang}/${path}`;
    return processCdnPath(fullPath);
  } catch (e) {
    console.error('Image path processing error:', e);
    return path;
  }
}

/**
 * 获取通用图片路径（common目录）
 * @param {string} path - 图片相对路径（相对于common目录）
 * @returns {string} 完整图片路径
 */
export function getCommonImagePath(path) {
  if (!path || typeof path !== 'string') {
    return path;
  }
  const prefix = getImagePrefix();
  const fullPath = `${prefix}/common/${path}`;
  return processCdnPath(fullPath);
}

/**
 * 获取根目录图片路径
 * @param {string} path - 图片文件名
 * @returns {string} 完整图片路径
 */
export function getRootImagePath(path) {
  if (!path || typeof path !== 'string') {
    return path;
  }
  const prefix = getImagePrefix();
  const fullPath = `${prefix}/${path}`;
  return processCdnPath(fullPath);
}

/**
 * 获取 CDN 配置（用于视频等媒体资源）
 * 优先从 window.__INITIAL_SITE_CONFIG__ 获取，否则使用构建时注入的全局变量
 * @returns {{ useCdn: boolean, cdnPrefix: string }}
 */
function getCdnConfig() {
  // 优先从运行时配置获取
  const config = getSiteConfig();
  if (config) {
    return {
      useCdn: config.useCdn || false,
      cdnPrefix: config.cdnPrefix || ''
    };
  }
  
  // 使用构建时注入的全局变量
  if (typeof __CDN_USE__ !== 'undefined' && typeof __CDN_PREFIX__ !== 'undefined') {
    return {
      useCdn: __CDN_USE__,
      cdnPrefix: __CDN_PREFIX__
    };
  }
  
  return { useCdn: false, cdnPrefix: '' };
}

/**
 * 获取 API 域名配置（用于图片资源）
 * @returns {string} API 域名
 */
function getApiDomainName() {
  const config = getSiteConfig();
  return config?.ApiDomainName || '';
}

/**
 * 视频文件扩展名列表
 */
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

/**
 * 检查路径是否为视频文件
 * @param {string} path - 文件路径
 * @returns {boolean} 是否为视频文件
 */
function isVideoFile(path) {
  if (!path || typeof path !== 'string') return false;
  const lowerPath = path.toLowerCase();
  return VIDEO_EXTENSIONS.some(ext => lowerPath.includes(ext));
}

/**
 * 获取媒体资源的完整 URL
 * - 视频文件（.mp4 等）使用 cdnPrefix
 * - 其他资源根据 useCdn 配置决定使用 cdnPrefix 或 ApiDomainName
 * @param {string} path - 媒体资源路径，如 '/img/v2/teacher/ru/xxx.mp4'
 * @returns {string} 带前缀的完整路径或原路径
 */
export function getMediaUrl(path) {
  // 如果路径为空或不是字符串，直接返回
  if (!path || typeof path !== 'string') {
    return path;
  }
  
  // 如果已经是完整的 URL，直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // 检查是否为视频文件
  const isVideo = isVideoFile(path);
  
  // 视频文件始终使用 cdnPrefix（只要 cdnPrefix 存在）
  if (isVideo) {
    const { cdnPrefix } = getCdnConfig();
    if (cdnPrefix) {
      return addDomainPrefix(path, cdnPrefix);
    }
    return path;
  }
  
  // 非视频文件（图片等）使用图片路径处理逻辑
  if (path.startsWith('/images/') || path.startsWith('/website/images/')) {
    return getImagePath(path);
  }
  
  // 其他路径，使用 ApiDomainName
  const apiDomainName = getApiDomainName();
  if (apiDomainName && path.startsWith('/')) {
    return `${apiDomainName}${path}`;
  }
  
  return path;
}

/**
 * 判断属性名是否为图片相关字段
 * @param {string} key - 属性名
 * @returns {boolean} 是否为图片相关字段
 */
function isImageField(key) {
  if (!key || typeof key !== 'string') return false;
  const lowerKey = key.toLowerCase();
  return lowerKey.includes('image') || 
         lowerKey.includes('icon') || 
         lowerKey.includes('qrcode') || 
         lowerKey.includes('font');
}

/**
 * 判断路径是否为图片路径
 * @param {string} path - 路径
 * @returns {boolean} 是否为图片路径
 */
function isImagePath(path) {
  if (!path || typeof path !== 'string') return false;
  return path.includes('/images/') || path.includes('/website/images/');
}

/**
 * 处理课程图片路径
 * 自动处理开发环境和生产环境的路径差异
 * @param {Object} courseData - 课程数据对象
 * @returns {Object} 处理后的课程数据对象
 */
export function processCourseImages(courseData) {
  // 如果输入为 null 或 undefined，返回空对象
  if (!courseData) return {};
  
  // 深拷贝对象
  let processed;
  try {
    processed = JSON.parse(JSON.stringify(courseData));
  } catch (e) {
    console.error('处理课程数据时出错:', e);
    return courseData;
  }
  
  /**
   * 递归处理对象中的所有属性
   * @param {Object} obj - 要处理的对象
   */
  function processObject(obj) {
    if (!obj || typeof obj !== 'object') return;
    
    // 处理数组
    if (Array.isArray(obj)) {
      obj.forEach(item => {
        if (typeof item === 'object' && item !== null) {
          processObject(item);
        }
      });
      return;
    }
    
    // 处理对象
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      
      const value = obj[key];
      
      // 跳过 Promise 对象
      if (value instanceof Promise) continue;
      
      // 处理字符串值
      if (typeof value === 'string') {
        // 检查是否为视频文件（优先判断，视频使用 cdnPrefix）
        if (isVideoFile(value)) {
          obj[key] = getMediaUrl(value);
        }
        // 处理图片路径
        else if (isImageField(key) && isImagePath(value)) {
          obj[key] = getImagePath(value);
        }
      }
      // 递归处理对象
      else if (typeof value === 'object' && value !== null) {
        processObject(value);
      }
    }
  }
  
  // 执行递归处理
  try {
    processObject(processed);
    return processed;
  } catch (e) {
    console.error('处理课程图片路径时出错:', e);
    return courseData;
  }
}
